import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "What if tech interviews aren't bullshit",
  "description": "Every tech employee says interviews are broken. Every employer keeps interviewing. What gives?",
  "published": "2020-09-04T00:00:00.000Z",
  "hero": "./img/screenshot-1599239711073.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Every tech employee says interviews are broken. Every employer keeps interviewing. What gives?`}</p>
    <p>{`It must mean interviews are working. Neutral at worst.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Google: 90% of our engineers use the software you wrote (Homebrew), but you can’t invert a binary tree on a whiteboard so fuck off.`}</p>{`— Max Howell (@mxcl) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/mxcl/status/608682016205344768"
      }}>{`June 10, 2015`}</a></blockquote>
    <h2 {...{
      "id": "why-companies-interview"
    }}>{`Why companies interview`}</h2>
    <p>{`Ever tried to hire an engineer my friend? It's insane.`}</p>
    <p>{`I've hired engineers twice on my own and several times for my employer. Not as a hiring manager, as a big part of the interview process.`}</p>
    <p>{`Here's how it goes:`}</p>
    <p>{`You announce a position. You're flooded with applicants.`}</p>
    <p>{`50+ if you're a no-name tiny company with little money to your name. 500+ if you're a FAANG.`}</p>
    <p>{`Thoughtfully considering every application takes a few days and they all look the same. A blur of buzzwords, bad cover letters, and past experience.`}</p>
    <p>{`Post on a popular hiring platform and you get 50+ applicants per week. Sifting becomes a full-time job.`}</p>
    <p>{`Paying an engineer to read job applications would be expensive `}<em parentName="p">{`and`}</em>{` they'd hate you. You hire a recruiter.`}</p>
    <h3 {...{
      "id": "everyone-looks-the-same"
    }}>{`Everyone looks the same`}</h3>
    <p>{`Your recruiter doesn't understand engineering. A good recruiter can hold their own in a 10min conversation.`}</p>
    <p>{`Buzzwords help. But anyone can put anything on a resume.`}</p>
    <p>{`Recruiters can't know 10 years of ReactJS experience is a lie. React, Vue, and jQuery are indistinguishable.`}</p>
    <p>{`Even if recruiters knew technology, everyone looks samesies from the outside. You went to so and so school (I don't care) and you had good grades (I don't care) and you've been coding in X, Y, and Z for N years (I care a little) and you've done nothing interesting.`}</p>
    <p>{`Please stand out. I want you to stand out. `}<strong parentName="p">{`Be a standout!`}</strong>{` Show something cool, `}<em parentName="p">{`tell me what you've achieved`}</em>{`.`}</p>
    <h3 {...{
      "id": "nobodys-good"
    }}>{`Nobody's good`}</h3>
    <p>{`Consider who applies for jobs. 🤔`}</p>
    <p>{`People who are on the market. The longer you're on the market, the more jobs you apply for. And the less likely you are to be good at your job.`}</p>
    <p>{`Or at least at interviewing.`}</p>
    <p>{`What's worse, true talent never makes it onto the market. You hire them through friends of friends while they're busy and entice them to switch jobs.`}</p>
    <p><em parentName="p">{`The best talent never applies for a job.`}</em>{` 🤯`}</p>
    <p>{`HackerNews has a monthly "Who wants to be hired" thread. You post there when you have a job, aren't quite looking, and might be interested in something new.`}</p>
    <p>{`PS: yes, newbies are by default on the market. No they aren't good at their job yet.`}</p>
    <h2 {...{
      "id": "3-types-of-roles-and-employees"
    }}>{`3 types of roles and employees`}</h2>
    <p>{`There's 3 types of jobs you might be interviewing for.`}</p>
    <p>{`You won't be told which. You have to guess. Your interviewer might not even understand this.`}</p>
    <p>{`But `}<em parentName="p">{`you`}</em>{` are the expert my friend, `}<em parentName="p">{`you`}</em>{` have to navigate this nuance. And use these signals to decide, if this is the right job for you. 😉`}</p>
    <h3 {...{
      "id": "1-the-blank-slate"
    }}>{`1. the blank slate`}</h3>
    <p>{`Newbies are a blank slate. They think they're the shit and really they're just shit.`}</p>
    <p>{`I kid, they're great 😛`}</p>
    <p>{`BUT they take a lot of hand holding. For the first 3 to 6 months these folks cost more than they produce.`}</p>
    <p>{`You have to pay them `}<em parentName="p">{`and`}</em>{` the senior engineer who's mentoring. Without that mentor they'll make a mess. With a bad mentor, they'll be ruined for years to come.`}</p>
    <p>{`Blank slates are great because you teach them how to be `}<em parentName="p">{`your`}</em>{` engineer. A perfect fit for your company and culture and how you do things.`}</p>
    <h3 {...{
      "id": "2-the-task-runner"
    }}>{`2. the task runner`}</h3>
    <p>{`A level up from blank slates are the task runners.`}</p>
    <p>{`Engineers who are good at their job, solve problems, and get shit done. The person who can do all your repetitive work.`}</p>
    <p>{`They're the line cooks of a kitchen. Give them a recipe and a process to follow and they're fantastic. Ask them to invent a new process or framework and they fall apart.`}</p>
    <p>{`You need these on your team.`}</p>
    <h3 {...{
      "id": "3-the-high-value-expert"
    }}>{`3. the high value expert`}</h3>
    <p>{`This is what you want to be. The sweet spot.`}</p>
    <p>{`As a high value expert your job is to solve new problems. Invent new frameworks, create new processes, teach the company how something works.`}</p>
    <p><em parentName="p">{`You`}</em>{` bring the expertise. `}<em parentName="p">{`You`}</em>{` tell them how it's done. `}<em parentName="p">{`You`}</em>{` create the process and the framework that Type 2 follows.`}</p>
    <p>{`Company hires you because you bring new skills to the table. You're solving problems they don't yet know how to solve.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`The farther into your career, the more your job is solving problems nobody knows the solution for. `}<br parentName="p"></br><br parentName="p"></br>{`At least nobody in your team, company, or org. Sometimes anywhere. You're first!`}<br parentName="p"></br><br parentName="p"></br>{`That's when your career takes off 🚀`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1300942542083485696"
      }}>{`September 1, 2020`}</a></blockquote>
    <h2 {...{
      "id": "what-interviews-measure"
    }}>{`What interviews measure`}</h2>
    <p>{`Interviews put you in a bucket. Are you type 1, 2, or 3?`}</p>
    <p><strong parentName="p">{`When you're new`}</strong>{`, the interview is designed to measure how teachable you are.`}</p>
    <p>{`Can we mold you into `}<em parentName="p">{`our`}</em>{` engineer? That's all the company cares about. Your skills are meh and your expertise is lol. Can we teach you how to do the job?`}</p>
    <p>{`These interviews are a hidden IQ test. And a bit of hoop jumping to make sure you won't run away at the first sign of struggle.`}</p>
    <p><strong parentName="p">{`When you're intermediate/senior`}</strong>{`, the interview is designed to see if you're an expert yet.`}</p>
    <p>{`How do you deal with ambiguity? If we give you a fuzzy task, will you ask enough questions to design a solution? If our ask lacks context, will you find it?`}</p>
    <p>{`A big part at this stage is the skills test.`}</p>
    <p>{`When we give you something to do, can you do it? The skills test is important and easy to fail.`}</p>
    <p>{`Many engineers say they know shit they don't actually know. But when you're an intermediate or senior engineer, `}<em parentName="p">{`I don't have time to teach you`}</em>{`. I need to know you'll do your job.`}</p>
    <p>{`Part of trusting you'll do your job is ensuring you can apply what you know in a new context. That's `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/why-understanding-beats-knowledge/"
      }}>{`why understanding beats knowledge`}</a>{`.`}</p>
    <p><strong parentName="p">{`When you're an expert`}</strong>{` the interview changes.`}</p>
    <p>{`You get the skills test. Companies need you to implement the solutions you create. First version at least. This should be trivial. If it isn't, you're not ready.`}</p>
    <p>{`Fuzzy questions are what matters. Your job is going to be `}<em parentName="p">{`"We have this problem, please solve it"`}</em></p>
    <p>{`Nobody knows the exact problem. Nobody knows the solution. All they know is where it hurts. You might even need to figure out where it hurts in the first place.`}</p>
    <p>{`At the expert stage a big part of your job is to say `}<em parentName="p">{`"Hey we keep running into this issue, we can do A, B, C to fix it, the tradeoffs and costs are X, Y, Z. Which should I start implementing?`}</em></p>
    <p>{`And that's when you get paid the big bux my friend 😉`}</p>
    <p>{`An expert level interview seeks `}<strong parentName="p">{`opinions`}</strong>{`. Do you have strong opinions about topics and can you back them up?`}</p>
    <h2 {...{
      "id": "why-interviews-suck"
    }}>{`Why interviews suck`}</h2>
    <p>{`Interviews suck for 2 reasons:`}</p>
    <ol>
      <li parentName="ol">{`you're interviewing for the wrong type of employee`}</li>
      <li parentName="ol">{`nobody teaches interviewers how to interview`}</li>
    </ol>
    <p>{`Happy Friday ❤️`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      